import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MoreVertical, Plus, Trash2, Check, Edit, FolderX, CheckCircle, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useInvoicePrefix } from "@/hooks/useInvoicePrefix";
import { DocumentType } from "@/types/form";
import {
  InvoicePrefixNumberFormData,
  invoicePrefixNumberSchema,
  InvoiceTypeCode,
} from "@/schema/schema";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { invoiceTypes } from "@/constants/invoiceTypes";
import { Skeleton } from "@/components/ui/skeleton";

const cleanupBodyLock = () => {
  document.body.style.removeProperty('pointer-events');
  document.body.style.removeProperty('overflow');
};

const InvoicePrefixNumber = () => {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [isRemoving, setIsRemoving] = React.useState<boolean>(false);
  const [selectedPrefix, setSelectedPrefix] =
    React.useState<InvoicePrefixNumberFormData | null>(null);
  const [currentTypeCode, setCurrentTypeCode] = React.useState<string>("01");
  const includedConsolidatedInvoiceType = [
    ...invoiceTypes,
    { "Code": "01A", "Description": "Consolidated" },
    { "Code": "GI", "Description": "Stock In" },
    { "Code": "GO", "Description": "Stock Out" },
    { "Code": "QT", "Description": "Quotation" },
    { "Code": "SO", "Description": "Sales Order" },
    { "Code": "DO", "Description": "Delivery Order" },
  ];
  const queryClient = useQueryClient();

  const {
    prefixNumbers,
    createPrefixNumberAsync,
    isCreatingPrefix,
    updatePrefixNumberAsync,
    deletePrefixNumberAsync,
    error,
  } = useInvoicePrefix(currentTypeCode as DocumentType);

  const getSchema = (selectedPrefix: InvoicePrefixNumberFormData | null) =>
    invoicePrefixNumberSchema.refine(
      (data) =>
        (selectedPrefix !== null && data.prefix === selectedPrefix.prefix ?
        data.lastNumber >= selectedPrefix.lastNumber : true),
      {
        message: "Avoid lower last number with the same prefix",
        path: ["lastNumber"],
      }
    );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<InvoicePrefixNumberFormData>({
    resolver: zodResolver(
      getSchema(selectedPrefix)
    ),
    mode: "all"
  });

  const onSubmit = async (data: InvoicePrefixNumberFormData) => {
    if (data.id === "NEW") {
      const { id, preview, ...formData } = data;
      await createPrefixNumberAsync(
        formData as Omit<InvoicePrefixNumberFormData, "id">,
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["prefixNumbers"] });
            toast({
              title: "Prefix Number Added",
              description: "The prefix invoice number successfully added.",
              icon: <CheckCircle size={30} className="animate-spin-once text-green-400"/>,
              duration: 5000,
            });
          },
          onError: (error: any) => {
            toast({
              title: "Failed",
              description: error?.response?.data?.message || error?.message || `Fail to create prefix invoice number. Please try again.`,
              icon: <AlertCircle size={30} className="animate-spin-once"/>,
              variant: "destructive",
              duration: 3000,
            });
          },
          onSettled: () => setIsOpen(false)
        }
      );
    } else {
      const { preview, id, ...formData } = data;
      await updatePrefixNumberAsync(
        { id: data.id, formData: formData },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["prefixNumbers"] });
            toast({
              title: "Prefix Number Changed",
              description:
                "The prefix invoice number successfully changed.",
              icon: <CheckCircle size={30} className="animate-spin-once text-green-400"/>,
              duration: 5000,
            });
          },
          onError: (error: any) => {
            toast({
              title: "Failed",
              description: error?.response?.data?.message || error?.message || `Fail to update prefix number. Please try again.`,
              icon: <AlertCircle size={30} className="animate-spin-once"/>,
              variant: "destructive",
              duration: 3000,
            });
          },
          onSettled: () => setIsOpen(false)
        }
      );
    }

    reset();  
  };

  const handleUpdatePrefix = async (id: string) => {
    const data = {
      isDefault: true,
      typeCode: currentTypeCode as InvoiceTypeCode
    };
    await updatePrefixNumberAsync(
      { id: id, formData: data },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["prefixNumbers"] });
          toast({
            title: "Default Prefix Number Changed",
            description:
              "The default prefix invoice number successfully changed.",
            duration: 5000,
            icon: <CheckCircle size={30} className="animate-spin-once text-green-400"/>,
          });
        },
        onError: (error: any) => {
          toast({
            title: "Failed",
            description: error?.response?.data?.message || error?.message || `Fail to update default prefix number. Please try again.`,
            variant: "destructive",
            icon: <AlertCircle size={30} className="animate-spin-once"/>,
            duration: 3000,
          });
        },
      }
    );
  };

  const handleRemovePrefix = async (id: string) => {
    await deletePrefixNumberAsync(id, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["prefixNumbers"] });
        toast({
          title: "Removed",
          description: `Prefix ${selectedPrefix?.prefix}${selectedPrefix?.lastNumber} successfully removed.`,
          icon: <CheckCircle size={30} className="animate-spin-once text-green-400"/>,
          duration: 3000,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Failed",
          description: error?.response?.data?.message || error?.message || `Fail to remove ${selectedPrefix?.prefix}${selectedPrefix?.lastNumber}. Please try again.`,
          icon: <AlertCircle size={30} className="animate-spin-once"/>,
          duration: 3000,
        });
      },
      onSettled: () => {setSelectedPrefix(null); setIsRemoving(false);},
    });
  };

  const [id, prefix, lastNumber] = watch(["id", "prefix", "lastNumber"]);

  React.useEffect(() => {
    if (isOpen) {
      setValue("preview", `${prefix ?? ""}${lastNumber ?? ""}`);
    }
  }, [prefix, lastNumber, setValue, isOpen]);

  React.useEffect(() => {
    cleanupBodyLock();
    return cleanupBodyLock;
  }, [isOpen, isRemoving]);

  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="w-full h-full px-6">
      <Card>
        <CardHeader>
          <CardTitle>Invoice Prefix Number</CardTitle>
          <CardDescription>
            Manage your auto generated e-Invoice number
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="w-full flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shadow"
              onClick={() => {
                setIsOpen(true);
                setSelectedPrefix(null);
                reset({
                  id: "NEW",
                  isDefault: false,
                  prefix: "",
                  lastNumber: undefined,
                  typeCode: currentTypeCode as InvoiceTypeCode,
                });
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          </div>
          <Tabs defaultValue={currentTypeCode}>
            <TabsList className="w-full justify-between">
              {includedConsolidatedInvoiceType.map((type) => (
                <TabsTrigger
                  onClick={() => setCurrentTypeCode(type.Code)}
                  key={type.Code}
                  value={type.Code}
                >
                  {type.Description}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={currentTypeCode}>
              <Table>
                <TableHeader className="bg-slate-50 dark:bg-slate-800">
                  <TableRow>
                    <TableHead>Prefix</TableHead>
                    <TableHead>Last Number</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {prefixNumbers?.data ? (
                    prefixNumbers.data.length > 0 ? (
                      prefixNumbers.data.map((prefix) => (
                        <TableRow key={prefix.id}>
                          <TableCell className="font-semibold">
                            {prefix.prefix}
                          </TableCell>
                          <TableCell>{prefix.lastNumber}</TableCell>
                          <TableCell>
                            {prefix.isDefault ? "DEFAULT" : ""}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" type="button" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  className="leading-none flex items-center"
                                  onClick={() => handleUpdatePrefix(prefix.id)}
                                  disabled={prefix.isDefault}
                                >
                                  <Check />
                                  Set as default
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="leading-none flex items-center"
                                  onClick={() => {
                                    setIsOpen(true);
                                    reset(prefix);
                                    setSelectedPrefix(prefix);
                                  }}
                                >
                                  <Edit />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="leading-none flex items-center text-red-500"
                                  disabled={prefix.isDefault}
                                  onClick={() => {
                                    setSelectedPrefix(prefix);
                                    setIsRemoving(true);
                                  }}
                                >
                                  <Trash2 color="red" />
                                  Remove
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="italic text-center text-lg text-gray-400"
                        >
                          <div className="flex flex-col justify-center items-center">
                            <FolderX className="h-40 w-40" />
                            <p>
                              Records are empty. Please add new prefix number.
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Skeleton className="size-full"/>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>

          {/* Create new / edit prefix modal */}
          <Dialog
            open={isOpen}
            onOpenChange={() => {
              setIsOpen(false);
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>  
                <DialogTitle>Create New Prefix</DialogTitle>
                <DialogDescription>
                  Fill in all required fields
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="w-full flex flex-col gap-5"
              >
                <div className="w-full flex flex-col gap-2">
                  <Controller
                    name="typeCode"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Label htmlFor="typeCode" required>
                          Invoice Type
                        </Label>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={id !== "NEW"}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent className="max-h-64 overflow-y-auto">
                            {includedConsolidatedInvoiceType.map((prefix) => (
                              <SelectItem key={prefix.Code} value={prefix.Code}>
                                {prefix.Code} - {prefix.Description}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  />
                  {errors.typeCode && (
                    <p className="text-red-600 text-sm">
                      {errors.typeCode.message}
                    </p>
                  )}
                  <Label htmlFor="prefix" required>
                    Prefix
                  </Label>
                  <Input
                    id="prefix"
                    placeholder="E.g. INV"
                    {...register("prefix")}
                  />
                  {errors.prefix && (
                    <p className="text-red-600 text-sm">
                      {errors.prefix.message}
                    </p>
                  )}

                  <Label htmlFor="lastNumber" required>
                    Last Number
                  </Label>
                  <Input
                    id="lastNumber"
                    placeholder="E.g. 1000"
                    {...register("lastNumber")}
                  />
                  {errors.lastNumber && (
                    <p className="text-red-600 text-sm">
                      {errors.lastNumber.message}
                    </p>
                  )}
                  <Controller
                    name="preview"
                    control={control}
                    render={({ field }) => (
                      <>
                        <Label htmlFor="preview">Preview</Label>
                        <Input {...field} id="preview" disabled />
                        {errors.preview && (
                          <p className="text-red-600 text-sm">
                            {errors.preview.message}
                          </p>
                        )}
                      </>
                    )}
                  />
                </div>
                <DialogFooter>
                  <Button
                    size="sm"
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsOpen(false);
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" type="submit" disabled={isCreatingPrefix}>
                    {isCreatingPrefix ? "Saving..." : "Save"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Remove selected prefix modal */}
          <Dialog open={isRemoving} onOpenChange={() => setIsRemoving(false)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirmation</DialogTitle>
                <DialogDescription>
                  Are you sure you want to remove{" "}
                  {`${selectedPrefix?.prefix}${selectedPrefix?.lastNumber}`}?
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setIsRemoving(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleRemovePrefix(selectedPrefix!.id)}
                >
                  Confirm
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoicePrefixNumber;
